import React, { useState } from 'react';
import {
  getProgramLabel,
  getPositionLabel,
  getResolvedAttachment,
  getProgramTitle,
  getAlternativeHotelLabel,
  getProgramExperienceLabel,
} from '../labelGetters';
import TranslationDisplay from './TranslationDisplay';
import ModifiedBadge from './ModifiedBadge';
import FieldLabel from './FieldLabel';

export function ProgramIncludedOptionDisplay({
  option,
  isNew = false,
  labels,
}) {
  const displayName =
    option.experienceName ||
    getProgramExperienceLabel(option.experienceId, labels) ||
    `Experience #${option.experienceId}`;
  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';

  return (
    <div
      className={`flex items-center gap-2 p-2 bg-gray-50 rounded border ${borderClass}`}
    >
      <i className="fa-regular fa-check-circle text-green-500" />
      <span className="text-sm text-gray-700">{displayName}</span>
    </div>
  );
}

export function ProgramSuggestedOptionDisplay({
  option,
  isNew = false,
  labels,
}) {
  const displayName =
    option.experienceName ||
    getProgramExperienceLabel(option.experienceId, labels) ||
    `Experience #${option.experienceId}`;
  // Handle both formats: requiredExperiences/suggestedExperiences (from labels) and required/suggested (from data)
  const requiredExperiences = option.requiredExperiences || [];
  const suggestedExperiences = option.suggestedExperiences || [];
  // Data format uses arrays of IDs, labels format uses arrays of objects with name
  const requiredIds = option.required || [];
  const suggestedIds = option.suggested || [];
  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';

  const hasRequiredData =
    requiredExperiences.length > 0 || requiredIds.length > 0;
  const hasSuggestedData =
    suggestedExperiences.length > 0 || suggestedIds.length > 0;

  return (
    <div className={`p-2 bg-gray-50 rounded border ${borderClass}`}>
      <div className="flex items-center gap-2">
        <i className="fa-regular fa-lightbulb text-yellow-500" />
        <span className="text-sm text-gray-700">{displayName}</span>
      </div>
      {(hasRequiredData || hasSuggestedData) && (
        <div className="mt-1 ml-5 space-y-1">
          {hasRequiredData && (
            <div className="text-xs text-gray-500">
              <FieldLabel>Required</FieldLabel>
              {requiredExperiences.length > 0
                ? requiredExperiences.map((e) => e.name).join(', ')
                : requiredIds.join(', ')}
            </div>
          )}
          {hasSuggestedData && (
            <div className="text-xs text-gray-500">
              <FieldLabel>Suggested</FieldLabel>
              {suggestedExperiences.length > 0
                ? suggestedExperiences.map((e) => e.name).join(', ')
                : suggestedIds.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Display for modified suggested option showing what changed
export function ProgramSuggestedOptionDiff({
  beforeOption,
  afterOption,
  labels,
}) {
  const displayName =
    afterOption?.experienceName ||
    beforeOption?.experienceName ||
    getProgramExperienceLabel(
      afterOption?.experienceId || beforeOption?.experienceId,
      labels,
    ) ||
    `Experience #${afterOption?.experienceId || beforeOption?.experienceId}`;

  const beforeRequired = beforeOption?.required || [];
  const afterRequired = afterOption?.required || [];
  const beforeSuggested = beforeOption?.suggested || [];
  const afterSuggested = afterOption?.suggested || [];

  const requiredChanged =
    JSON.stringify(beforeRequired) !== JSON.stringify(afterRequired);
  const suggestedChanged =
    JSON.stringify(beforeSuggested) !== JSON.stringify(afterSuggested);

  return (
    <div className="p-2 bg-gray-50 rounded border border-amber-200">
      <div className="flex items-center gap-2">
        <i className="fa-regular fa-lightbulb text-yellow-500" />
        <span className="text-sm text-gray-700">{displayName}</span>
        <ModifiedBadge />
      </div>
      <div className="mt-1 ml-5 space-y-1 text-xs">
        {requiredChanged && (
          <div className="text-gray-500">
            <FieldLabel>Required</FieldLabel>
            {beforeRequired.length > 0 ? beforeRequired.length : 'none'} →{' '}
            {afterRequired.length > 0 ? afterRequired.length : 'none'}
          </div>
        )}
        {suggestedChanged && (
          <div className="text-gray-500">
            <FieldLabel>Suggested</FieldLabel>
            {beforeSuggested.length > 0
              ? beforeSuggested.length
              : 'none'} →{' '}
            {afterSuggested.length > 0 ? afterSuggested.length : 'none'}
          </div>
        )}
      </div>
    </div>
  );
}

export function ProgramAlternativeHotelDisplay({
  hotel,
  isNew = false,
  labels,
}) {
  const displayName =
    hotel.hotelName ||
    getAlternativeHotelLabel(hotel.hotelId, labels) ||
    `Hotel #${hotel.hotelId || hotel.id}`;
  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';

  return (
    <div
      className={`flex items-center gap-2 p-2 bg-gray-50 rounded border ${borderClass}`}
    >
      <i className="fa-regular fa-hotel text-indigo-500" />
      <span className="text-sm text-gray-700">{displayName}</span>
    </div>
  );
}

export function ProgramExperienceDisplay({
  experience,
  isNew = false,
  labels,
}) {
  const displayName =
    experience.experienceName ||
    getProgramExperienceLabel(experience.experienceId, labels) ||
    `Experience #${experience.experienceId || experience.id}`;
  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';

  return (
    <div
      className={`flex items-center gap-2 p-2 bg-gray-50 rounded border ${borderClass}`}
    >
      <i className="fa-regular fa-layer-group text-blue-500" />
      <span className="text-sm text-gray-700">{displayName}</span>
    </div>
  );
}

export function ProgramDisplay({
  program,
  labels,
  isNew = false,
  attachmentLabels,
}) {
  const [translationsExpanded, setTranslationsExpanded] = useState(false);
  const programLabel = getProgramLabel(program.id, labels);
  const title = getProgramTitle(program, labels);

  const startPositionName =
    programLabel?.startPosition?.name ||
    getPositionLabel(program.startPositionId, labels, program.id) ||
    (program.startPositionId ? `Position #${program.startPositionId}` : null);

  const endPositionName =
    programLabel?.endPosition?.name ||
    getPositionLabel(program.endPositionId, labels, program.id) ||
    (program.endPositionId ? `Position #${program.endPositionId}` : null);

  // Get accommodation hotel name from labels
  const accommodationHotelName =
    programLabel?.accomodationHotel?.displayName ||
    (program.accomodationHotelId
      ? `Hotel #${program.accomodationHotelId}`
      : null);

  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';
  const translations = program.translations || [];

  return (
    <div className={`p-2 bg-gray-50 rounded border ${borderClass}`}>
      <div className="flex items-center gap-2 mb-1">
        <i className="fa-regular fa-calendar-day text-indigo-500" />
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </div>
      {(startPositionName || endPositionName) && (
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          {startPositionName && <span>{startPositionName}</span>}
          {startPositionName && endPositionName && (
            <i className="fa-regular fa-arrow-right text-gray-400" />
          )}
          {endPositionName && <span>{endPositionName}</span>}
        </div>
      )}
      {accommodationHotelName && (
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <i className="fa-regular fa-hotel text-gray-400" />
          <span>{accommodationHotelName}</span>
        </div>
      )}
      {program.attachments?.length > 0 && (
        <div className="mt-2 flex gap-1">
          {program.attachments.map((att) => {
            const resolvedAtt = getResolvedAttachment(att, attachmentLabels);
            return (
              <div
                key={att.id}
                className="w-8 h-8 bg-gray-200 rounded overflow-hidden"
              >
                {resolvedAtt?.imageUrl ? (
                  <img
                    src={resolvedAtt.imageUrl}
                    alt={resolvedAtt.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <i className="fa-regular fa-image text-gray-400 text-xs" />
                )}
              </div>
            );
          })}
        </div>
      )}
      {programLabel?.includedOptions?.length > 0 && (
        <div className="mt-2">
          <div className="mb-1">
            <FieldLabel>Included</FieldLabel>
          </div>
          <div className="text-xs text-gray-600">
            {programLabel.includedOptions
              .slice(0, 2)
              .map((opt) => opt.experienceName)
              .join(', ')}
            {programLabel.includedOptions.length > 2 &&
              ` +${programLabel.includedOptions.length - 2} more`}
          </div>
        </div>
      )}
      {programLabel?.suggestedOptions?.length > 0 && (
        <div className="mt-1">
          <div className="mb-1">
            <FieldLabel>Suggested</FieldLabel>
          </div>
          <div className="text-xs text-gray-600">
            {programLabel.suggestedOptions
              .slice(0, 2)
              .map((opt) => opt.experienceName)
              .join(', ')}
            {programLabel.suggestedOptions.length > 2 &&
              ` +${programLabel.suggestedOptions.length - 2} more`}
          </div>
        </div>
      )}
      {programLabel?.alternativeHotels?.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          <i className="fa-regular fa-hotel text-gray-400 mr-1" />
          {programLabel.alternativeHotels.length} alternative hotel(s)
        </div>
      )}
      {programLabel?.programExperiences?.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          <i className="fa-regular fa-layer-group text-blue-400 mr-1" />
          {programLabel.programExperiences.length} experience(s)
        </div>
      )}
      {translations.length > 0 && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 w-full border-none"
            onClick={() => setTranslationsExpanded(!translationsExpanded)}
          >
            <i
              className={`fa-regular fa-chevron-${translationsExpanded ? 'down' : 'right'} text-gray-400 w-3`}
            />
            <i className="fa-regular fa-language text-gray-400" />
            <span>Translations ({translations.length})</span>
          </button>
          {translationsExpanded && (
            <div className="mt-2 space-y-2">
              {translations.map((translation) => (
                <TranslationDisplay
                  key={translation.locale || translation.id}
                  translation={translation}
                  isNew={isNew}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
